import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { Columns, Figure, InlineBar, Ledger } from "~/components/charts";
import { api, type ApiResponse, type PaginatedResponse } from "~/lib/api";
import { periodWindow, toPeriod } from "~/lib/period";
import { commitsDek, commitsHeadline } from "../story";
import { fmtFixed, periodName, fmtDay } from "~/lib/format";
import { ed, field, tbl } from "~/components/editorial";

interface Commit {
  id: string;
  sha: string;
  shortSha: string;
  message: string;
  messageFirstLine: string;
  authorName: string;
  authorEmail: string | null;
  additions: number;
  deletions: number;
  linesChanged: number;
  committedAt: string;
  author: {
    id: string;
    displayName: string;
    githubUsername: string | null;
    avatarUrl: string | null;
  } | null;
  repository: {
    id: string;
    name: string;
    fullName: string;
  };
}

interface CommitStats {
  totalCommits: number;
  totalAdditions: number;
  totalDeletions: number;
  avgAdditions: number;
  avgDeletions: number;
  topAuthors: Array<{
    author: {
      id: string;
      displayName: string;
      githubUsername: string | null;
      avatarUrl: string | null;
    } | null;
    commitCount: number;
    additions: number;
    deletions: number;
  }>;
  commitsByDayOfWeek: Array<{ name: string; count: number }>;
  commitsByHour: Array<{ hour: number; label: string; count: number }>;
}

interface CommitTimeline {
  date: string;
  commits: number;
  additions: number;
  deletions: number;
}

interface CommitsTabProps {
  range: string;
}

export function CommitsTab({ range }: CommitsTabProps) {
  const [search, setSearch] = useState("");
  const win = periodWindow(toPeriod(range));
  const now = periodName(win.label);

  const { data: commits, isLoading: isLoadingCommits } = useQuery({
    queryKey: ["commits", { range, search }],
    queryFn: () =>
      api.get<PaginatedResponse<Commit>>(
        `/commits?range=${range}&limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`,
      ),
    staleTime: 30 * 1000,
  });

  const { data: stats } = useQuery({
    queryKey: ["commits-stats", range],
    queryFn: () =>
      api.get<ApiResponse<CommitStats>>(`/commits/stats?range=${range}`),
    staleTime: 60 * 1000,
  });

  const { data: timeline } = useQuery({
    queryKey: ["commits-timeline", range],
    queryFn: () =>
      api.get<ApiResponse<CommitTimeline[]>>(
        `/commits/timeline?range=${range}`,
      ),
    staleTime: 60 * 1000,
  });

  const commitList = commits?.data.items ?? [];
  const commitStats = stats?.data;
  const timelineData = timeline?.data ?? [];

  const byDay = commitStats?.commitsByDayOfWeek ?? [];
  const byHour = commitStats?.commitsByHour ?? [];
  const busiestDay = byDay.reduce<{ name: string; count: number } | null>(
    (b, d) => (d.count > (b?.count ?? 0) ? d : b),
    null,
  );
  const busiestHour = byHour.reduce<{ label: string; count: number } | null>(
    (b, d) => (d.count > (b?.count ?? 0) ? d : b),
    null,
  );
  const peakDay = timelineData.reduce<CommitTimeline | null>(
    (b, d) => (d.commits > (b?.commits ?? 0) ? d : b),
    null,
  );
  const authors = commitStats?.topAuthors ?? [];
  const topAuthor = authors[0];
  const maxAuthorCommits = Math.max(1, ...authors.map((a) => a.commitCount));
  const total = commitStats?.totalCommits ?? 0;

  return (
    <>
      <div className={ed.kicker}>Commits · {now}</div>
      <h1 className={ed.headlineCompact}>
        {commitStats
          ? commitsHeadline(
              total,
              commitStats.totalAdditions,
              commitStats.totalDeletions,
              now,
            )
          : "Commits"}
      </h1>
      {commitStats && (
        <p className={ed.dek}>
          {commitsDek(
            busiestDay,
            busiestHour,
            topAuthor && total > 0
              ? {
                  name: topAuthor.author?.displayName ?? "An unlinked author",
                  count: topAuthor.commitCount,
                  share: Math.round((topAuthor.commitCount / total) * 100),
                }
              : null,
          )}
        </p>
      )}
      <div className={ed.byline}>
        <b>Commits</b> · by commit date, {win.label}, across synced
        repositories. From GitHub.
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          { label: "Commits", value: fmtFixed(commitStats?.totalCommits) },
          { label: "Lines added", value: fmtFixed(commitStats?.totalAdditions) },
          {
            label: "Lines removed",
            value: fmtFixed(commitStats?.totalDeletions),
          },
          {
            label: "Lines per commit",
            value: commitStats
              ? fmtFixed(commitStats.avgAdditions + commitStats.avgDeletions)
              : "–",
            note: commitStats
              ? `+${fmtFixed(commitStats.avgAdditions)} −${fmtFixed(commitStats.avgDeletions)} average`
              : undefined,
          },
        ]}
      />

      <div className={ed.sect}>I. When the work lands</div>
      <h2 className={ed.h2}>
        {peakDay
          ? `Commits peaked on ${fmtDay(peakDay.date)} with ${fmtFixed(peakDay.commits)}`
          : `No commits yet in ${now}`}
      </h2>
      <Figure
        title="Commits per day"
        sub="Each column is one calendar day; the peak in color."
        method="Commits by commit date (author's clock), all synced repositories and branches."
        table={{
          columns: ["Day", "Commits", "Added", "Removed"],
          rows: timelineData.map((d) => [
            fmtDay(d.date),
            d.commits,
            d.additions,
            d.deletions,
          ]),
        }}
      >
        {timelineData.length > 0 ? (
          <Columns
            points={timelineData.map((d) => ({
              key: d.date,
              label: fmtDay(d.date),
              value: d.commits,
            }))}
            ariaLabel="Commits per day"
            unit="commits"
            emphasizeMax
          />
        ) : (
          <div className={ed.loading}>No commits yet.</div>
        )}
      </Figure>

      <div className={ed.halves} style={{ marginTop: 28 }}>
        <Figure
          title="By day of week"
          sub="The busiest day in color."
          method="All commits in the window, grouped by weekday of the commit date."
          table={{
            columns: ["Day", "Commits"],
            rows: byDay.map((d) => [d.name, d.count]),
          }}
        >
          <Columns
            points={byDay.map((d) => ({
              key: d.name,
              label: d.name.slice(0, 3),
              title: d.name,
              value: d.count,
            }))}
            ariaLabel="Commits by day of week"
            unit="commits"
            emphasizeMax
            valueLabels
            width={560}
          />
        </Figure>
        <Figure
          title="By hour of day"
          sub="The busiest hour in color."
          method="All commits in the window, grouped by hour of the commit timestamp as the server stores it."
          table={{
            columns: ["Hour", "Commits"],
            rows: byHour.map((d) => [d.label, d.count]),
          }}
        >
          <Columns
            points={byHour.map((d) => ({
              key: String(d.hour),
              label: d.label,
              value: d.count,
            }))}
            ariaLabel="Commits by hour"
            unit="commits"
            emphasizeMax
            width={560}
          />
        </Figure>
      </div>

      {authors.length > 0 && (
        <>
          <div className={ed.sect}>II. Who committed</div>
          <h2 className={ed.h2}>
            {topAuthor?.author?.displayName ?? "One author"} made the most
            commits
          </h2>
          <div className={tbl.scroll}>
            <table className={tbl.numeric}>
              <thead>
                <tr>
                  <th data-num>#</th>
                  <th>Author</th>
                  <th data-num>Commits</th>
                  <th />
                  <th data-num>Share</th>
                  <th data-num>Added</th>
                  <th data-num>Removed</th>
                </tr>
              </thead>
              <tbody>
                {authors.map((item, i) => (
                  <tr key={item.author?.id ?? `unknown-${i}`}>
                    <td data-num data-dim>
                      {i + 1}
                    </td>
                    <td data-text className={styles.name}>
                      {item.author ? (
                        <>
                          <Link to={`/developers/${item.author.id}`}>
                            {item.author.displayName}
                          </Link>
                          {item.author.githubUsername && (
                            <span className={styles.handle}>
                              @{item.author.githubUsername}
                            </span>
                          )}
                        </>
                      ) : (
                        "Unknown"
                      )}
                    </td>
                    <td data-num>{fmtFixed(item.commitCount)}</td>
                    <td>
                      <InlineBar
                        value={item.commitCount}
                        max={maxAuthorCommits}
                        width={80}
                      />
                    </td>
                    <td data-num data-dim>
                      {total > 0
                        ? `${Math.round((item.commitCount / total) * 100)}%`
                        : "–"}
                    </td>
                    <td data-num>+{fmtFixed(item.additions)}</td>
                    <td data-num>−{fmtFixed(item.deletions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={ed.methodNarrow}>
            Top authors by commit count. Commits from emails not linked to a
            developer show as Unknown.
          </p>
        </>
      )}

      <div className={ed.sect}>III. The log</div>
      <h2 className={ed.h2}>
        {commitList.length > 0
          ? `The latest ${fmtFixed(commitList.length)} commits`
          : "The latest commits"}
      </h2>
      <div className={tbl.controls}>
        <input
          type="search"
          placeholder="Search commit messages"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={field.search}
          aria-label="Search commits"
        />
      </div>
      {isLoadingCommits ? (
        <div className={ed.loading}>Loading commits…</div>
      ) : commitList.length > 0 ? (
        <div className={tbl.scroll}>
          <table className={tbl.numeric}>
            <thead>
              <tr>
                <th>When</th>
                <th>SHA</th>
                <th>Message</th>
                <th>Repo</th>
                <th>Author</th>
                <th data-num>Added</th>
                <th data-num>Removed</th>
              </tr>
            </thead>
            <tbody>
              {commitList.map((commit) => (
                <tr key={commit.id}>
                  <td data-dim>
                    {new Date(commit.committedAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </td>
                  <td>
                    <a
                      href={`https://github.com/${commit.repository.fullName}/commit/${commit.sha}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {commit.shortSha}
                    </a>
                  </td>
                  <td data-text className={styles.message}>
                    {commit.messageFirstLine}
                  </td>
                  <td data-text className={styles.dimText}>
                    {commit.repository.name}
                  </td>
                  <td data-text className={styles.nowrap}>
                    {commit.author?.displayName ?? commit.authorName}
                  </td>
                  <td data-num>+{fmtFixed(commit.additions)}</td>
                  <td data-num>−{fmtFixed(commit.deletions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={ed.loading}>No commits found.</div>
      )}
    </>
  );
}

const styles = {
  name: css({ whiteSpace: "nowrap!", "& a": { fontWeight: 600 } }),
  handle: css({
    ml: "6px",
    fontFamily: "mono",
    fontSize: "10.5px",
    color: "ed.ink3",
  }),
  message: css({ minWidth: "320px", lineHeight: 1.35 }),
  dimText: css({
    color: "ed.ink2!",
    whiteSpace: "nowrap!",
    fontSize: "11.5px",
  }),
  nowrap: css({ whiteSpace: "nowrap!" }),
};
